import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, FileText, RefreshCw, X } from 'lucide-react';
import Sheet from './Sheet';
import { api, errorMessage } from '../lib/api';
import { cn, fmtDate, qty } from '../lib/utils';

/* What Claude read off a photographed delivery note: the header, every line
   next to the order line it belongs to, and the open order lines the note
   leaves out. Reading happens in the background after saving, so a note that
   is still being read polls until it is done. */
export default function DeliveryNoteSheet({ noteId, onClose }) {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.deliveryNoteLines(noteId);
      setState({ loading: false, data });
      return data;
    } catch (err) {
      setState({ loading: false, error: errorMessage(err) });
      return null;
    }
  }, [noteId]);

  useEffect(() => {
    load();
  }, [load]);

  const status = state.data?.status;
  useEffect(() => {
    if (status !== 'pending') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [status, load]);

  const reread = async () => {
    setBusy(true);
    try {
      await api.rereadDeliveryNote(noteId);
      await load();
    } catch (err) {
      setState((s) => ({ ...s, error: errorMessage(err) }));
    } finally {
      setBusy(false);
    }
  };

  const d = state.data;
  const note = d?.note;
  const h = d?.header || {};
  const lines = d?.lines || [];
  const missing = d?.open_on_order_not_on_note || [];
  const s = d?.summary || {};

  return (
    <Sheet open onClose={onClose} label="What the delivery note says" className="sheet-tall">
      <div className="flex flex-col px-4 pb-3 pt-3" style={{ height: 'calc(100% - 12px)' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-cond truncate text-[24px] font-bold leading-none">{note ? note.po_names.map((n) => n.split(' ')[0]).join(' · ') : 'Delivery note'}</p>
            <p className="mt-1 truncate text-[13px] text-ink2">
              {[note?.supplier, h.document_number && `No. ${h.document_number}`, h.document_date && fmtDate(h.document_date)].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="-mx-1 mt-3 min-h-0 flex-1 overflow-y-auto px-1 pb-2">
          {state.loading && <p className="text-[14px] text-ink2">Loading…</p>}
          {state.error && <p className="rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{state.error}</p>}

          {d && !d.found && (
            <div className="rounded-xl bg-sunken px-3 py-3 text-[14px]">
              {status === 'pending' && <p className="font-semibold">Reading the note… this takes a minute or two.</p>}
              {status === 'failed' && <p><span className="font-semibold text-alert">Could not be read.</span> <span className="text-ink2">{d.error}</span></p>}
              {status === 'off' && <p className="text-ink2">Automatic reading was off when this note was saved.</p>}
              {!status && <p className="text-ink2">This note has not been read yet.</p>}
            </div>
          )}

          {d?.found && (
            <>
              {/* the shipment as a whole */}
              <div className="flex flex-wrap gap-1.5">
                <Chip>{s.lines} {s.lines === 1 ? 'line' : 'lines'}</Chip>
                <Chip>{qty(s.units_on_note)} units</Chip>
                {s.amount_on_note != null && <Chip>{h.currency || ''} {s.amount_on_note}</Chip>}
                {s.not_on_order > 0 && <Chip tone="alert">{s.not_on_order} not on order</Chip>}
                {s.code_differs > 0 && <Chip tone="warn">{s.code_differs} other code</Chip>}
                {s.part_of_set > 0 && <Chip>{s.part_of_set} box lines of a set</Chip>}
                {s.more_than_ordered > 0 && <Chip tone="alert">{s.more_than_ordered} more than ordered</Chip>}
                {s.low_confidence_lines > 0 && <Chip tone="warn">{s.low_confidence_lines} hard to read</Chip>}
                {missing.length > 0 && <Chip tone="warn">{missing.length} open not on note</Chip>}
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-xl bg-sunken px-3 py-2.5 text-[13px]">
                <Field label="Cartons" value={h.packages_total} />
                <Field label="Pallets" value={h.pallets_total} />
                <Field label="Gross weight" value={h.gross_weight_kg != null ? `${h.gross_weight_kg} kg` : null} />
                <Field label="Volume" value={h.volume_m3 != null ? `${h.volume_m3} m³` : null} />
                <Field label="Carrier" value={h.carrier} />
                <Field label="Delivery date" value={h.delivery_date && fmtDate(h.delivery_date)} />
                <Field label="Tracking" value={(h.tracking_numbers || []).join(', ') || null} wide />
                <Field label="Orders printed" value={(h.order_references || []).join(', ') || null} wide />
                <Field label="Remarks" value={h.remarks} wide />
              </dl>
              {d.unreadable && !/^none\b/i.test(d.unreadable) && (
                <details className="tone-crossdock mt-2 rounded-xl bg-tone-bg px-3 py-2 text-[13px]">
                  <summary className="flex cursor-pointer items-center gap-2 font-semibold text-tone">
                    <AlertTriangle className="h-4 w-4 shrink-0" /> Part of the note was hard to read · details
                  </summary>
                  <p className="mt-1">{d.unreadable}</p>
                </details>
              )}

              {/* every line, next to the order line it belongs to */}
              <h3 className="mt-4 text-[13px] font-semibold text-ink2">Lines on the note</h3>
              <ul className="mt-1.5 space-y-1.5">
                {lines.map((l, i) => (
                  <LineCard key={i} line={l} />
                ))}
              </ul>

              {missing.length > 0 && (
                <>
                  <h3 className="mt-4 text-[13px] font-semibold text-ink2">Still open on the order, not on the note</h3>
                  <ul className="mt-1.5 divide-y divide-line rounded-xl border border-line">
                    {missing.map((m, i) => (
                      <li key={i} className="flex items-center gap-3 px-3 py-2">
                        <span className="min-w-0 flex-1">
                          <span className="clamp-2 block text-[13px] font-semibold leading-tight">{m.product}</span>
                          <span className="block font-mono text-[12px] text-ink2">{[m.vendor_code, m.purchase_order?.split(' ')[0]].filter(Boolean).join(' · ')}</span>
                          {m.likely_on_note_as && <span className="tone-crossdock block text-[12px] font-semibold text-tone">Probably on the note as: {m.likely_on_note_as.join(', ')}</span>}
                        </span>
                        <span className="shrink-0 text-[13px] font-semibold">{qty(m.still_open)} open</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 gap-2 pt-2">
          {note?.pdf_url && (
            <a href={note.pdf_url} target="_blank" rel="noreferrer" className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-sunken text-[14px] font-semibold active:bg-line">
              <FileText className="h-4 w-4" /> PDF
            </a>
          )}
          {d && status !== 'pending' && (
            <button type="button" onClick={reread} disabled={busy} className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-sunken text-[14px] font-semibold active:bg-line disabled:opacity-50">
              <RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} /> {d.found ? 'Read again' : 'Read it now'}
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function Chip({ children, tone }) {
  return (
    <span className={cn('rounded-full px-2.5 py-1 text-[12px] font-semibold', tone === 'alert' ? 'bg-alert-bg text-alert' : tone === 'warn' ? 'tone-crossdock bg-tone-bg text-tone' : 'bg-sunken text-ink')}>
      {children}
    </span>
  );
}

function Field({ label, value, wide }) {
  if (value == null || value === '') return null;
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink2">{label}</dt>
      <dd className="break-words font-semibold">{value}</dd>
    </div>
  );
}

function LineCard({ line }) {
  const m = line.match;
  const flag = line.flag;
  return (
    <li className={cn('rounded-xl border px-3 py-2', flag === 'not_on_order' || flag === 'more_than_ordered' ? 'border-alert/40' : flag === 'code_differs' ? 'border-line' : 'border-line')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="clamp-2 text-[14px] font-semibold leading-tight">{line.description || '—'}</p>
          <p className="mt-0.5 font-mono text-[12px] text-ink2">
            {[line.vendor_code, line.ean && `EAN ${line.ean}`, line.lot_or_serial && `lot ${line.lot_or_serial}`].filter(Boolean).join(' · ') || 'no code printed'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-cond text-[22px] font-bold leading-none">
            {line.quantity != null ? qty(line.quantity) : '?'}
            <span className="text-[13px] font-semibold text-ink2"> {line.unit || ''}</span>
          </p>
          {(line.packages != null || line.amount != null) && (
            <p className="text-[12px] text-ink2">{[line.packages != null && `${qty(line.packages)} ctn`, line.amount != null && `${line.amount}`].filter(Boolean).join(' · ')}</p>
          )}
        </div>
      </div>
      {m && (
        <p className="mt-1.5 flex items-start gap-1.5 text-[12px]">
          <Check className="mt-px h-3.5 w-3.5 shrink-0 text-out" />
          <span className="min-w-0">
            <span className="font-semibold">{m.purchase_order?.split(' ')[0]}</span>
            <span className="text-ink2"> · ordered {qty(m.ordered)} · received {qty(m.received)} · open {qty(m.still_open)}</span>
            {flag === 'more_than_ordered' && <span className="font-semibold text-alert"> · more than ordered</span>}
            {flag === 'part_of_set' && <span className="block text-ink2">One box of {m.product}: the supplier prints one line per box.</span>}
            {m.also_on && <span className="text-ink2"> · also on {m.also_on.map((p) => p.split(' ')[0]).join(', ')}</span>}
          </span>
        </p>
      )}
      {flag === 'not_on_order' && <p className="mt-1.5 text-[12px] font-semibold text-alert">Not on the order</p>}
      {flag === 'code_differs' && (
        <div className="mt-1.5 text-[12px]">
          <p className="font-semibold text-tone tone-crossdock">Other code than on the order. Most likely:</p>
          <ul className="mt-0.5 space-y-0.5">
            {(line.possible_matches || []).map((c, i) => (
              <li key={i} className="text-ink2">
                <span className="font-mono">{c.our_vendor_code}</span> {c.product} <span className="font-semibold text-ink">({c.purchase_order?.split(' ')[0]}, {qty(c.still_open)} open)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {line.confidence === 'low' && <p className="mt-1 text-[12px] font-semibold text-alert">Hard to read: check the page</p>}
      {line.remark && <p className="mt-1 text-[12px] text-ink2">{line.remark}</p>}
    </li>
  );
}
